import Link from "next/link";
import { redirect } from "next/navigation";
import { auth, signOut } from "@/auth";
import { Container } from "@/components/layout/container";
import { prisma } from "@/lib/prisma";
import { updateProfile } from "./actions";
import { SubmitButton } from "@/components/ui/submit-button";

function getOrderStatusLabel(status: string) {
  switch (status) {
    case "PENDING":
      return "En attente";
    case "PAID":
      return "Payée";
    case "PREPARING":
      return "En préparation";
    case "SHIPPED":
      return "Expédiée";
    case "COMPLETED":
      return "Terminée";
    case "CANCELLED":
      return "Annulée";
    default:
      return status;
  }
}

export default async function ComptePage({
  searchParams,
}: {
  searchParams?: Promise<{ saved?: string; completeProfile?: string }>;
}) {
  const session = await auth();

  if (!session?.user) {
    redirect("/connexion?callbackUrl=/compte");
  }

  const params = await searchParams;

  const orders = await prisma.order.findMany({
    where: {
      userId: session.user.id,
    },
    include: {
      items: true,
    },
    orderBy: {
      createdAt: "desc",
    },
  });

  const user = await prisma.user.findUnique({
    where: { id: session.user.id },
  });

  const fullName =
    [session.user.firstName, session.user.lastName]
      .filter(Boolean)
      .join(" ")
      .trim() || session.user.name || "Cliente";

  return (
    <section
      className="section min-h-screen"
      style={{
        background:
          "linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)",
      }}
    >
      <Container>
        <div className="mx-auto max-w-6xl">
          {params?.saved === "1" ? (
            <div className="mb-6 rounded-[22px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] px-5 py-4 text-sm text-[var(--foreground)] shadow-[var(--shadow)]">
              Vos informations ont bien été enregistrées.
            </div>
          ) : null}

          {params?.completeProfile === "1" ? (
            <div className="mb-6 rounded-[22px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] px-5 py-4 text-sm text-[var(--foreground)] shadow-[var(--shadow)]">
              Merci de compléter votre téléphone et votre adresse avant de finaliser une commande.
            </div>
          ) : null}

          <div className="mb-10 max-w-3xl">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85">
              Espace client
            </p>
            <h1 className="mt-4 text-5xl leading-[0.96] sm:text-6xl">
              Bonjour {session.user.firstName || fullName}
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-7 text-[var(--foreground)]/74">
              Retrouvez ici les informations de votre compte, l’accès à votre panier
              et bientôt le suivi de vos commandes.
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
            <div className="grid gap-6">
            <section className="rounded-[30px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] p-6 shadow-[var(--shadow)] sm:p-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--primary)]">
                  Mon profil
                </p>

                <form action={updateProfile} className="mt-6 space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <input
                      name="firstName"
                      placeholder="Prénom"
                      defaultValue={user?.firstName || ""}
                      className="w-full rounded-2xl border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] px-4 py-3 outline-none"
                    />
                    <input
                      name="lastName"
                      placeholder="Nom"
                      defaultValue={user?.lastName || ""}
                      className="w-full rounded-2xl border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] px-4 py-3 outline-none"
                    />
                  </div>

                  <input
                    name="phone"
                    placeholder="Téléphone"
                    defaultValue={user?.phone || ""}
                    className="w-full rounded-2xl border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] px-4 py-3 outline-none"
                  />

                  <input
                    name="address"
                    placeholder="Adresse"
                    defaultValue={user?.address || ""}
                    className="w-full rounded-2xl border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] px-4 py-3 outline-none"
                  />

                  <div className="grid gap-4 sm:grid-cols-2">
                    <input
                      name="postalCode"
                      placeholder="Code postal"
                      defaultValue={user?.postalCode || ""}
                      className="w-full rounded-2xl border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] px-4 py-3 outline-none"
                    />
                    <input
                      name="city"
                      placeholder="Ville"
                      defaultValue={user?.city || ""}
                      className="w-full rounded-2xl border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] px-4 py-3 outline-none"
                    />
                  </div>

                  <div className="pt-2 [&_button]:cursor-pointer [&_button]:transition [&_button]:duration-300 [&_button]:hover:-translate-y-0.5 [&_button]:hover:brightness-95 [&_button]:active:translate-y-0">
  <SubmitButton />
</div>
                </form>
              </section>

              <section className="rounded-[30px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] p-6 shadow-[var(--shadow)] sm:p-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85">
                  Accès rapides
                </p>

                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  <QuickLink
                    href="/panier"
                    title="Mon panier"
                    text="Retrouver les produits et kits sélectionnés avant le paiement."
                  />
                  <QuickLink
                    href="/boutique"
                    title="Continuer mes achats"
                    text="Explorer à nouveau la boutique et découvrir les nouvelles harmonies."
                  />
                  <QuickLink
                    href="/specificites-des-produits"
                    title="Spécificités des produits"
                    text="Relire les informations matière, formats et différences entre les laines."
                  />
                  <QuickLink
                    href="/conseils-d-entretien"
                    title="Conseils d’entretien"
                    text="Retrouver les bons gestes pour préserver la qualité des laines."
                  />
                  {session.user.role === "ADMIN" ? (
                    <>
                      <QuickLink
                        href="/admin/commandes"
                        title="Administration commandes"
                        text="Accéder au suivi des commandes, consulter les achats et mettre à jour les statuts."
                      />
                      <QuickLink
                        href="/admin/articles"
                        title="Administration articles"
                        text="Gérer les fiches produits, les images, la publication et l'organisation du catalogue."
                      />
                    </>
                  ) : null}
                </div>
              </section>
            </div>

            <div className="grid gap-6">
              <section className="rounded-[30px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] p-6 shadow-[var(--shadow)] sm:p-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85">
                  Commandes
                </p>

                {orders.length === 0 ? (
                  <>
                    <h2 className="mt-3 text-3xl leading-tight">
                      Aucune commande pour le moment.
                    </h2>
                    <p className="mt-4 text-sm leading-7 text-[var(--foreground)]/74">
                      Vos commandes apparaîtront ici après un achat validé.
                    </p>

                    <div className="mt-6 rounded-[22px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] p-5">
                      <p className="text-sm leading-7 text-[var(--foreground)]/72">
                        Vous pouvez déjà préparer votre sélection depuis le panier et finaliser votre commande depuis la boutique.
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <h2 className="mt-3 text-3xl leading-tight">
                      Retrouvez l’historique de vos commandes.
                    </h2>
                    <p className="mt-4 text-sm leading-7 text-[var(--foreground)]/74">
                      Chaque commande validée apparaît ici avec son statut, son montant, les références achetées et les principales informations de livraison.
                    </p>

                    <div className="mt-6 flex flex-col gap-4">
                      {orders.map((order) => (
                        <details
                          key={order.id}
                          className="group rounded-[24px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] shadow-[var(--shadow)] transition open:bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)]"
                        >
                          <summary className="flex cursor-pointer list-none flex-col gap-4 p-5 marker:content-none sm:flex-row sm:items-start sm:justify-between">
                            <div>
                              <p className="text-sm font-medium tracking-[0.01em] text-[var(--foreground)]/82">
                                {order.orderNumber}
                              </p>
                              <p className="mt-1 text-xs text-[var(--foreground)]/58">
                                Commande passée le {new Date(order.createdAt).toLocaleDateString("fr-FR")}
                              </p>
                            </div>

                            <div className="flex flex-wrap items-center gap-2">
                              <span className="rounded-full bg-[var(--primary)]/10 px-3 py-1 text-[11px] font-medium text-[var(--primary)]">
                                {order.items.length} article{order.items.length > 1 ? "s" : ""}
                              </span>
                              <span className="rounded-full border border-[var(--border)] px-3 py-1 text-[11px] text-[var(--foreground)]/65">
                                {Number(order.total).toFixed(2)} €
                              </span>
                              <span className="rounded-full border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] px-3 py-1 text-[11px] text-[var(--foreground)]/75">
                                Statut : {getOrderStatusLabel(order.status)}
                              </span>
                              <span className="ml-1 inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] text-[var(--foreground)]/65 transition group-open:rotate-180 group-open:border-[var(--primary)] group-open:text-[var(--primary)]">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.9"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="h-4 w-4"
                                >
                                  <path d="m6 9 6 6 6-6" />
                                </svg>
                              </span>
                            </div>
                          </summary>

                          <div className="border-t border-[var(--border)]/70 px-5 pb-5 pt-4">
                            <div className="grid gap-3 sm:grid-cols-2">
                              <OrderInfoLine
                                label="Paiement"
                                value={order.paymentStatus === "PAID" ? "Paiement confirmé" : order.paymentStatus}
                              />
                              <OrderInfoLine
                                label="Téléphone"
                                value={order.customerPhone || user?.phone || "Non renseigné"}
                              />
                              <OrderInfoLine
                                label="Adresse de livraison"
                                value={order.shippingAddressText || user?.address || "Non renseignée"}
                                fullWidth
                              />
                            </div>

                            <div className="mt-4 rounded-[20px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] p-4">
                              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--foreground)]/55">
                                Détail de la commande
                              </p>

                              <div className="mt-3 flex flex-wrap gap-2">
                                {order.items.map((item) => (
                                  <span
                                    key={item.id}
                                    className="rounded-full border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] px-3 py-1 text-xs text-[var(--foreground)]/72"
                                  >
                                    {item.productNameSnapshot} × {item.quantity}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </details>
                      ))}
                    </div>
                  </>
                )}
              </section>

              <section className="border-t border-[var(--border)] pt-10">
                <div className="max-w-lg">
                  <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--primary)]">
                    Session
                  </p>

                  <div className="mt-5 flex items-center justify-between gap-6 border-b border-[var(--border)]/80 pb-5">
                    <div>
                      <p className="text-lg leading-tight text-[var(--foreground)]/84">
                        Se déconnecter
                      </p>
                      <p className="mt-2 text-sm leading-6 text-[var(--foreground)]/62">
                        Quitter votre espace client en toute simplicité.
                      </p>
                    </div>

                    <form
                      action={async () => {
                        "use server";
                        await signOut({ redirectTo: "/" });
                      }}
                    >
                      <button
                        type="submit"
                        className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] px-5 py-2.5 text-sm font-medium transition hover:border-[var(--accent)] hover:text-[var(--accent)]"
                      >
                        <span>Déconnexion</span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="h-4 w-4"
                        >
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                          <path d="m16 17 5-5-5-5" />
                          <path d="M21 12H9" />
                        </svg>
                      </button>
                    </form>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

function InfoCard({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-[22px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] p-5">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--foreground)]/55">
        {label}
      </p>
      <p className="mt-3 break-words text-sm leading-6 text-[var(--foreground)]/78">
        {value}
      </p>
    </div>
  );
}

function OrderInfoLine({
  label,
  value,
  fullWidth = false,
}: {
  label: string;
  value: string;
  fullWidth?: boolean;
}) {
  return (
    <div
      className={[
        "rounded-[18px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] p-4 transition",
        fullWidth ? "sm:col-span-2" : "",
      ].join(" ")}
    >
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--foreground)]/55">
        {label}
      </p>
      <p className="mt-2 whitespace-pre-line break-words text-sm leading-6 text-[var(--foreground)]/78">
        {value}
      </p>
    </div>
  );
}

function QuickLink({
  href,
  title,
  text,
}: {
  href: string;
  title: string;
  text: string;
}) {
  return (
    <Link
      href={href}
      className="group rounded-[24px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] p-5 transition hover:-translate-y-0.5 hover:border-[var(--primary)]"
    >
      <h3 className="text-xl leading-tight">{title}</h3>
      <p className="mt-3 text-sm leading-6 text-[var(--foreground)]/72">
        {text}
      </p>
      <span className="mt-5 inline-flex text-sm font-medium text-[var(--accent)] transition group-hover:text-[var(--primary)]">
        Ouvrir
      </span>
    </Link>
  );
}